/*
 * This file is used to collect environment variables and set reasonable defaults.
 * This allows other code paths to reference this file to ensure we are consistent
 */

import { ProductGame } from "./@types/common";

/**
 * String with all supported locales.
 * These are the locales that will be built by default, unless overriden by the
 * environment variable `LOCALES`.
 */
export const ALL_SUPPORTED_LOCALES =
  "cs-cz de-de el-gr en-us es-es es-mx fr-fr hu-hu id-id it-it ja-jp ko-kr ms-my pl-pl pt-br ro-ro ru-ru th-th tr-tr vn-vn vi-vn zh-tw";

export const REDEEM_ONLY_LOCALES = ["ko-kr", "vn-vn", "vi-vn", "zh-tw"];

/**
 * Array with all supported wildrift locales.
 */
export const VALID_WR_LOCALES = ["en-us", "id-id", "ms-my", "th-th"];

/**
 * Array with all supported league of legends locales.
 */
export const VALID_LOL_LOCALES = [
  "cs-cz",
  "de-de",
  "el-gr",
  "en-us",
  "es-es",
  "es-mx",
  "fr-fr",
  "hu-hu",
  "it-it",
  "ja-jp",
  "ko-kr",
  "pl-pl",
  "pt-br",
  "ro-ro",
  "ru-ru",
  "tr-tr",
  "vn-vn",
  "vi-vn",
  "zh-tw",
];

export const DEFAULT_LOCALE = "en-us";

const buildEnv = process.env.GATSBY_ENV || process.env.NODE_ENV || "production";
const isProduction = buildEnv === "production";

const siteConfig = {
  // NODE_ENV prints production in staging env, use GATSBY_ENV for workaround.
  BUILD_ENV: buildEnv,
  RIOTBAR_ENV: process.env.RIOTBAR_ENV || buildEnv,
  isProduction,

  NEW_RELIC: {
    LICENSE_KEY: "bf329d6c65",
    ACCOUNT_ID: "700268",
    TRUST_KEY: "1484199",
    AGENT_ID: isProduction ? "786423406" : "704648558",
  },
  ENABLE_VALORANT: process.env.ENABLE_VALORANT === "true",
  ENABLE_DYNAMIC_LANDING: process.env.ENABLE_DYNAMIC_LANDING === "true",
  ENABLE_PAID_RIOT_ID_PURCHASE: process.env.ENABLE_PAID_RIOT_ID_PURCHASE === "true",
  ENABLE_CASH_CODES: process.env.GATSBY_ENABLE_CASH_CODES === "true",
  DEFAULT_LOCALE: DEFAULT_LOCALE,
  AFFILIATE_PARAMETER_NAME: "cc",

  // Default configuration is for local || production
  API_VERSION: process.env.GATSBY_API_VERSION || "v1",
  API_BASE: process.env.GATSBY_API_BASE || "https://api.shop.riotgames.com",
  API_DEFAULT_TIMEOUT: process.env.GATSBY_API_TIMEOUT || "10000",
  CONTENTCODES_API_BASE:
    process.env.GATSBY_CONTENTCODES_API_BASE || "https://stage2-red.pp.sgp.pvp.net/contentcodes/v1",

  // Cash Codes overrides
  ENABLE_CASHCODES_API_BASE_OVERRIDE:
    !!process.env.GATSBY_RIOTPAY_PMC_BASE || !!process.env.GATSBY_RIOTPAY_LOCATION_BASE,

  // Session Storage Keys
  KEY_GAME_ID: "shop_riotgames_game_id",
  KEY_RSO_SESSION: "shop_riotgames_rso_session",
  KEY_SOURCE_ORIGIN: "shop_riotgames_source_origin",
  KEY_CHECKOUT_CONTEXT: "shop_riotgames_checkout_context",
  KEY_AFFILIATE: "shop_riotgames_affiliate",
  KEY_AFFILIATE_SESSION: "shop_riotgames_affiliate_session",
  KEY_SESSION_ID: "shop_riotgames_sessionid",
  KEY_LOGIN_ACTION_TYPE: "shop_riotgames_login_action_type",
  KEY_LOGIN_ACTION_DATA: "shop_riotgames_login_action_data",
  KEY_GATSBY_REACT_I18N_LANGUAGE: "gatsby-i18next-language",
  KEY_START_OF_FLOW_PAGE: "shop_riotgames_start_of_flow_page",

  // Content Code Storage Keys
  KEY_REDEEM_CODE: "shop_riotgames_redeem_code",
  KEY_REDEEM_CODE_VALIDATION_STATUS: "shop_riotgames_redeem_code_validation_status",
  KEY_REDEEM_REWARDS: "shop_riotgames_redeem_rewards",
  KEY_REDEEM_FULFILLMENT_ITEMS: "shop_riotgames_redeem_fulfillment_items",
  KEY_REDEEM_THEME: "shop_riotgames_redeem_theme",
  KEY_REDEEM_RIOTPAY_INSTANCE: "shop_riotgames_redeem_riotpay_instance",
  KEY_REDEEM_SESSION_TOKEN: "shop_riotgames_redeem_session_token",

  // Cash Code Storage Keys
  KEY_REDEEM_GAMES_PLAYED: "shop_riotgames_redeem_cash_code_games_played",
  KEY_REDEEM_CASH_CODE_VALIDATION_DETAILS: "shop_riotgames_redeem_cash_code_validation_details",
  KEY_REDEEM_CASH_CODE_REDEMPTION_DETAILS: "shop_riotgames_redeem_cash_code_redemption_details",
  KEY_REDEEM_CASH_CODE_BUYER_INFO: "shop_riotgames_redeem_cash_code_buyer_info",

  RSO_HOST: process.env.GATSBY_RSO_HOST || "https://auth.riotgames.com",
  RSO_CLIENT_ID: process.env.GATSBY_RSO_CLIENT_ID || "directbuy-prod",
  XSSO_HOST: process.env.GATSBY_XSSO_HOST || "https://xsso.riotgames.com",
};

if (!isProduction) {
  siteConfig.API_BASE = process.env.GATSBY_API_BASE || `https://${siteConfig.BUILD_ENV}.api.shop.riotgames.io`;
  siteConfig.API_DEFAULT_TIMEOUT = process.env.GATSBY_API_TIMEOUT || "60000";
  siteConfig.ENABLE_PAID_RIOT_ID_PURCHASE = true;
}

export default siteConfig;

// We expose game specific configs due to the nuiance of internal environments have seperate rso environments.
export const gameSpecificConfig: { [key: string]: any } = {
  [ProductGame.LeagueOfLegends]: {
    // default
    ...siteConfig,

    // Stage RSO
    RSO_HOST: process.env.GATSBY_RSO_HOST || "https://stage.auth.accounts.riotgames.com",
    RSO_CLIENT_ID: process.env.GATSBY_RSO_CLIENT_ID || `directcontent-web-${siteConfig.BUILD_ENV}`,
  },
  [ProductGame.WildRift]: {
    // default
    ...siteConfig,

    // WRINT RSO
    RSO_HOST: process.env.GATSBY_RSO_HOST || "https://auth.wr-int.rpg.riotgames.com",
    RSO_CLIENT_ID: process.env.GATSBY_RSO_CLIENT_ID || `directbuy-${siteConfig.BUILD_ENV}`,
  },
};

export const HOMEPAGE_BY_GAME: { [key in ProductGame]: string } = {
  [ProductGame.WildRift]: "https://wildrift.leagueoflegends.com",
  [ProductGame.LeagueOfLegends]: "https://www.leagueoflegends.com",
  [ProductGame.Valorant]: "https://playvalorant.com/",
  [ProductGame.LegendsOfRuneterra]: "https://playruneterra.com/",
};

export const CURRENCY_BY_GAME: { [key in ProductGame]: string } = {
  [ProductGame.WildRift]: "WildCores",
  [ProductGame.LeagueOfLegends]: "RP",
  [ProductGame.Valorant]: "VP",
  [ProductGame.LegendsOfRuneterra]: "Coins",
};

export const GAME_NAME_BY_GAMEID: { [key in ProductGame]: string } = {
  [ProductGame.WildRift]: "wildrift",
  [ProductGame.LeagueOfLegends]: "leagueoflegends",
  [ProductGame.Valorant]: "valorant",
  [ProductGame.LegendsOfRuneterra]: "lor",
};

export const defaultImageDimensions = {
  width: 1920,
  height: 1080,
};

export const defaultBannerDimensions = {
  width: 1046,
  height: 445,
};
